// src/payment/PaymentSuccess.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import useStore from '../config/store';

const PaymentSuccess = () => {
    const [status, setStatus] = useState('processing');
    const [initialData, setInitialData] = useState(null);
    const { organization } = useStore();

    useEffect(() => {
        if (!organization) return;

        // Suscripción a cambios en tiempo real del documento organization
        const unsubscribe = onSnapshot(
            doc(db, 'organization', organization),
            (doc) => {
                const data = doc.data();
                console.log(data);

                if (!initialData) {
                    setInitialData({
                        current_subscription_id: data.current_subscription_id,
                        last_payment_id: data.last_payment_id
                    });
                    return;
                }

                // Verificar si cambiaron current_subscription_id Y last_payment_id
                if (data.current_subscription_id !== initialData.current_subscription_id &&
                    data.last_payment_id !== initialData.last_payment_id) {
                    setStatus('completed');
                    setTimeout(() => window.close(), 3000);
                }
            },
            (error) => {
                console.error('Error listening to organization:', error);
                setStatus('failed');
            }
        );

        // Timeout después de 2 minutos
        const timeout = setTimeout(() => {
            setStatus('timeout');
        }, 120000);

        return () => {
            unsubscribe();
            clearTimeout(timeout);
        };
    }, [organization, initialData]);

    return (
        <Box sx={{
            p: 4,
            textAlign: 'center',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f5f5'
        }}>
            {status === 'processing' && (
                <Box>
                    <CircularProgress size={60} sx={{ color: '#1976d2' }} />
                    <Typography variant="h5" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>
                        Procesando tu suscripción...
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        PayPal está confirmando tu pago.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Una vez confimado el pago, procederemos con la activación de tu plan.
                    </Typography>
                    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                            Por favor mantén esta ventana abierta
                        </Typography>
                    </Box>
                </Box>
            )}

            {status === 'completed' && (
                <Box>
                    <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h4" sx={{ mb: 2, color: 'success.main', fontWeight: 'bold' }}>
                        ¡Suscripción activada!
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Tu plan ha sido activado correctamente.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Esta ventana se cerrará automáticamente en unos segundos.
                    </Typography>
                </Box>
            )}

            {status === 'failed' && (
                <Box>
                    <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                    <Typography variant="h5" sx={{ mb: 2, color: 'error.main', fontWeight: 'bold' }}>
                        Error al procesar la suscripción
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Hubo un problema al activar tu plan.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Por favor contacta a soporte o intenta nuevamente.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => window.close()}
                        sx={{ mt: 2 }}
                    >
                        Cerrar
                    </Button>
                </Box>
            )}

            {status === 'timeout' && (
                <Box>
                    <ScheduleIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
                    <Typography variant="h5" sx={{ mb: 2, color: 'warning.main', fontWeight: 'bold' }}>
                        Procesamiento tardando más de lo esperado
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        No hemos recibido confirmación del estado final.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Puedes cerrar esta ventana y verificar el estado de tu suscripción en unos minutos.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => window.close()}
                        sx={{ mt: 2 }}
                    >
                        Cerrar
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default PaymentSuccess;